import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const BRAND = {
  cyan: '#00AEEF',
  magenta: '#EC008C',
  red: '#ED1C24',
};

const clientLogos = [
  { id: 1, name: 'TechCorp', url: 'https://via.placeholder.com/150x60/f8fafc/64748b?text=TechCorp' },
  { id: 2, name: 'GlobalBank', url: 'https://via.placeholder.com/150x60/f8fafc/64748b?text=GlobalBank' },
  { id: 3, name: 'CityMall', url: 'https://via.placeholder.com/150x60/f8fafc/64748b?text=CityMall' },
  { id: 4, name: 'ApexResidences', url: 'https://via.placeholder.com/150x60/f8fafc/64748b?text=ApexResidences' },
  { id: 5, name: 'MetroHospitals', url: 'https://via.placeholder.com/150x60/f8fafc/64748b?text=MetroHospitals' },
  { id: 6, name: 'PrimeLogistics', url: 'https://via.placeholder.com/150x60/f8fafc/64748b?text=PrimeLogistics' },
];

const STATS = [
  { value: '24/7', label: 'Active Monitoring', color: BRAND.cyan },
  { value: '<3 min', label: 'Response Time', color: BRAND.magenta },
  { value: '100%', label: 'Vetted Personnel', color: BRAND.red },
];

const SERVICES = [
  {
    title: 'Manned Guarding',
    desc: 'Highly trained, vetted personnel for round-the-clock physical presence and rapid response.',
    color: BRAND.cyan,
    icon: (
      <>
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
  },
  {
    title: 'E-Surveillance',
    desc: 'Advanced CCTV monitoring, access control, and automated alarm systems.',
    color: BRAND.magenta,
    icon: (
      <>
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
  },
  {
    title: 'Access Control',
    desc: 'Smart entry management for residential complexes and commercial facilities.',
    color: BRAND.red,
    icon: (
      <>
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </>
    ),
  },
  {
    title: 'Emergency Response',
    desc: 'Coordinated enforcement protocols for incidents, evacuations, and crisis handling.',
    color: BRAND.cyan,
    icon: (
      <>
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </>
    ),
  },
];

function ServiceIcon({ color, children }) {
  return (
    <div
      className="flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110"
      style={{ background: `${color}14`, boxShadow: `0 0 20px ${color}18` }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </svg>
    </div>
  );
}

function ServiceCard({ service, delay, visible }) {
  return (
    <div
      className={`about-reveal about-card-shine group ${visible ? 'about-visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="relative h-full rounded-[1.35rem] p-[2px] ses-brand-gradient transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/80">
        <div className="relative h-full rounded-[1.25rem] bg-white p-6 overflow-hidden">
          <div
            className="absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-opacity"
            style={{ background: service.color }}
          />
          <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
          <h4 className="relative mt-4 mb-2 text-lg font-bold text-slate-900">{service.title}</h4>
          <p className="relative text-sm text-slate-500 leading-relaxed">{service.desc}</p>
        </div>
      </div>
    </div>
  );
}

export default function SecurityServices() {
  const [visible, setVisible] = useState(false);
  const pageRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={pageRef} className="w-full min-h-screen font-display overflow-x-hidden">
      {/* Page background */}
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-white via-slate-50 to-white pointer-events-none" />
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#00AEEF]/6 via-transparent to-[#ED1C24]/5 pointer-events-none" />

      {/* Hero */}
      <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 px-4 md:px-10 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 ses-brand-gradient opacity-60" />
        <div className="absolute top-1/4 -left-24 w-72 h-72 rounded-full bg-[#00AEEF]/10 blur-[90px] pointer-events-none" />
        <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-[#EC008C]/8 blur-[100px] pointer-events-none" />

        <div className={`relative max-w-6xl mx-auto text-center about-reveal ${visible ? 'about-visible' : ''}`}>
          <span className="mb-6 inline-flex items-center gap-2 rounded-full p-[1.5px] ses-brand-gradient shadow-sm">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BRAND.cyan} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
              </svg>
              Premium Protection
            </span>
          </span>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6">
            Uncompromising{' '}
            <span className="ses-brand-text">Security Solutions</span>
          </h1>

          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed mb-10">
            Advanced enforcement and security services tailored for residential complexes,
            commercial hubs, and corporate institutions. Your safety is our mandate.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-14">
            <Link
              to="/contact"
              className="relative overflow-hidden group px-8 py-3.5 rounded-2xl text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,174,239,0.35)]"
            >
              <span className="absolute inset-0 ses-brand-gradient opacity-90 group-hover:opacity-100 transition-opacity" />
              <span className="relative">Request Assessment</span>
            </Link>
            <a
              href="#security-services"
              className="px-8 py-3.5 rounded-2xl bg-white text-slate-700 font-semibold border border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md transition-all hover:-translate-y-0.5"
            >
              Our Services
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {STATS.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl bg-white border border-slate-200 px-5 py-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              >
                <p className="text-2xl font-black" style={{ color: stat.color }}>{stat.value}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client logo slider */}
      <section className="relative py-12 bg-white/60 border-y border-slate-200/80 overflow-hidden">
        <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-8">
          Trusted by industry leaders
        </p>
        <div className="w-full inline-flex flex-nowrap overflow-hidden [mask-image:linear-gradient(to_right,transparent,white_128px,white_calc(100%-128px),transparent)]">
          <ul className="flex items-center justify-center md:justify-start [&_li]:mx-8 [&_img]:max-w-none security-infinite-scroll">
            {[...clientLogos, ...clientLogos].map((logo, i) => (
              <li key={`${logo.id}-${i}`} aria-hidden={i >= clientLogos.length}>
                <img
                  src={logo.url}
                  alt={logo.name}
                  className="h-12 md:h-14 w-auto opacity-50 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* About + services */}
      <section id="security-services" className="relative py-20 lg:py-28 px-4 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-center mb-16">
            <div className={`about-reveal space-y-6 ${visible ? 'about-visible' : ''}`} style={{ transitionDelay: '150ms' }}>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
                Vigilance Meets{' '}
                <span className="ses-brand-text">Technology</span>
              </h2>
              <div className="h-1 w-24 rounded-full ses-brand-gradient" />
              <p className="text-slate-500 leading-relaxed text-lg">
                At Sai Enforcement And Security Pvt. Ltd, we don&apos;t just provide guards — we
                deliver comprehensive security ecosystems. With rigorous training and
                state-of-the-art surveillance, our enforcement protocols safeguard high-value
                assets and communities.
              </p>
            </div>

            <div
              className={`about-reveal relative rounded-[1.75rem] p-[2px] ses-brand-gradient shadow-xl ${visible ? 'about-visible' : ''}`}
              style={{ transitionDelay: '250ms' }}
            >
              <div className="relative h-[420px] md:h-[480px] rounded-[1.65rem] overflow-hidden bg-slate-100">
                <img
                  src="https://images.unsplash.com/photo-1557597774-9d273605dfa9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Security Professional"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 rounded-2xl p-[2px] ses-brand-gradient">
                  <div className="rounded-[0.9rem] bg-white/95 backdrop-blur-md p-5 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Response Time</p>
                      <p className="text-2xl font-black ses-brand-text">&lt; 3 Minutes</p>
                    </div>
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{ background: `${BRAND.cyan}14` }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BRAND.cyan} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Service cards grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((service, i) => (
              <ServiceCard key={service.title} service={service} delay={300 + i * 100} visible={visible} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative px-4 md:px-10 pb-20 lg:pb-28">
        <div
          className={`about-reveal max-w-4xl mx-auto rounded-3xl p-[2px] ses-brand-gradient shadow-xl ${visible ? 'about-visible' : ''}`}
          style={{ transitionDelay: '500ms' }}
        >
          <div className="rounded-[1.35rem] bg-white px-8 py-12 md:py-14 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Secure Your Premises{' '}
              <span className="ses-brand-text">Today</span>
            </h2>
            <p className="text-slate-500 mb-8 max-w-lg mx-auto leading-relaxed">
              Contact our security experts for a vulnerability assessment and custom deployment plan.
            </p>
            <Link
              to="/contact"
              className="relative inline-flex overflow-hidden group items-center gap-2 px-8 py-4 rounded-2xl text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(236,0,140,0.3)]"
            >
              <span className="absolute inset-0 ses-brand-gradient opacity-90 group-hover:opacity-100 transition-opacity" />
              <span className="relative">Contact Security Division</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="relative transition-transform group-hover:translate-x-1">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
